'use client';

/**
 * ChatPanel — Unified BFF-driven message list + compose
 *
 * Single strategy call: chat(entity_type, entity_sid)
 * Returns messages, contacts, compose context, and available channels.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain messaging
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Globe, MessageSquare, Loader2, Paperclip, FileText, Image, ScanLine } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PlatformBadge } from './details/shared';
import { useStrategyData, useExtractFromMessage, useSendWhatsApp, usePostToChatter } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { MessengerTabsSection } from './chat/sections/messenger-tabs-section';
import { cn } from '@/lib/utils/cn';
import { toast } from 'sonner';
import type { ContactSummary, MessengerChannel, WhatsAppMediaAttachment } from '@/lib/api/types';

interface ChatMessage {
  cdk: string;
  body: string;
  body_translated?: string | null;
  translated_to?: string | null;
  language?: string | null;
  author_name: string;
  author_id: string;
  platform: string;
  message_type: string;
  created_date: string;
  has_attachments: boolean;
  attachment_count: number;
  attachment_urls: string[] | null;
  attachment_metadata: string | null;
  // Group-aggregated tags. Set when this is a tender chat (x_rfq_group_id)
  // so the UI can label / filter per sibling vendor.
  rfq_sid?: string | null;
  rfq_name?: string | null;
  vendor_name?: string | null;
}

interface ComposeContext {
  label: string;
  model: string;
  id: number;
  type: string;
  sid: string;
}

interface ChatSibling {
  rfq_sid: string;
  rfq_name: string | null;
  vendor_name: string | null;
}

interface ChatData {
  entity_type: string;
  entity_sid: string;
  entity_name: string | null;
  messages: ChatMessage[];
  message_count: number;
  contacts: ContactSummary[];
  context_model: string | null;
  context_id: number | null;
  compose_contexts: ComposeContext[];
  channels: MessengerChannel[];
  x_rfq_group_id?: string | null;
  siblings?: ChatSibling[];
}

// Sentinel for the "All vendors" filter when chat is tender-aggregated.
const ALL_SIBLINGS = '__all__';

// ── Helpers ──────────────────────────────────────────────────────

function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '  • ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const AVATAR_COLORS = [
  'bg-primary/15 text-primary',
  'bg-success/15 text-success',
  'bg-warning/15 text-warning',
  'bg-info/15 text-info',
  'bg-destructive/15 text-destructive',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function parseAttachments(urls: string[] | null, metadataJson: string | null) {
  if (!urls || urls.length === 0) return [];
  let metadata: Array<{ filename: string; mimetype: string; size: number }> = [];
  if (metadataJson) {
    try { metadata = JSON.parse(metadataJson); } catch { /* fallback */ }
  }
  return urls.map((url, i) => {
    const meta = metadata[i] || { filename: '', mimetype: '', size: 0 };
    return {
      url,
      filename: meta.filename || url.split('/').pop() || 'attachment',
      mimetype: meta.mimetype || 'application/octet-stream',
      size: meta.size || 0,
    };
  });
}

function getFileIcon(mimetype: string) {
  if (mimetype.startsWith('image/')) return Image;
  if (mimetype === 'application/pdf') return FileText;
  return Paperclip;
}

const DI_ELIGIBLE_MIMETYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]);

function formatSize(bytes: number): string {
  if (bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(dateStr: string): string {
  const trimmed = dateStr.trim();
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(trimmed);
  const d = hasTimezone
    ? new Date(trimmed)
    : new Date(trimmed.replace(' ', 'T') + 'Z');
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Translation toggle (per-message state) ──────────────────────

function TranslatedBody({ msg }: { msg: ChatMessage }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const hasTranslation = !!msg.body_translated;
  const originalBody = stripHtml(msg.body);
  const translatedBody = msg.body_translated ? stripHtml(msg.body_translated) : null;
  const body = hasTranslation && !showOriginal ? translatedBody : originalBody;

  return (
    <>
      {hasTranslation && (
        <button
          onClick={() => setShowOriginal(!showOriginal)}
          className="inline-flex items-center gap-0.5 text-[9px] text-muted-foreground hover:text-foreground transition-colors mb-0.5"
          title={showOriginal ? 'View translation' : 'View original'}
        >
          <Globe className="h-2.5 w-2.5" />
          {showOriginal ? msg.language?.toUpperCase() : msg.translated_to?.toUpperCase()}
        </button>
      )}
      {body && (
        <p className="text-xs text-foreground whitespace-pre-wrap break-words leading-relaxed">
          {body}
        </p>
      )}
    </>
  );
}

// ── Component ────────────────────────────────────────────────────

interface ChatPanelProps {
  entityType: string | null;
  entityId: string | null;
}

export function ChatPanel({ entityType, entityId }: ChatPanelProps) {
  const queryClient = useQueryClient();
  const sendWhatsAppStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'send_whatsapp'));
  const chatterPostStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'chatter_post'));
  const extractStrategy = useCapabilityStore((s) => s.strategyName('document_intelligence', 'extract'));
  // Chat read — capability-resolved like everything else. Resolves to
  // `'chat'` via the (messaging, history) registration in
  // `ui_config/handlers/chat.py`.
  const chatHistoryStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'history'));
  const sendWhatsApp = useSendWhatsApp(sendWhatsAppStrategy!);
  const postChatter = usePostToChatter(chatterPostStrategy!);
  const extractMutation = useExtractFromMessage(extractStrategy!);

  // Language — shared store (canvas toggle, copilot reads)
  const lang = useLanguageStore((s) => s.lang);

  const { data, isLoading, error } = useStrategyData<ChatData>(
    chatHistoryStrategy ?? '',
    { entity_type: entityType ?? '', entity_sid: entityId ?? '', ...(lang ? { lang } : {}) },
    !!chatHistoryStrategy && !!entityType && !!entityId,
  );

  const chatData = data?.data as ChatData | undefined;
  const allMessages = chatData?.messages ?? [];
  const contacts = (chatData?.contacts ?? []) as ContactSummary[];
  const composeContexts = chatData?.compose_contexts ?? [];
  const channels = (chatData?.channels ?? []) as MessengerChannel[];
  const siblings = chatData?.siblings ?? [];
  const isTender = siblings.length > 1;

  // Active sibling tab — sentinel = "All vendors". Resets when the
  // user navigates to a different entity.
  const [activeSibling, setActiveSibling] = useState<string>(ALL_SIBLINGS);
  useEffect(() => { setActiveSibling(ALL_SIBLINGS); }, [entityId]);

  const countsBySibling = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of allMessages) {
      if (m.rfq_sid) counts.set(m.rfq_sid, (counts.get(m.rfq_sid) ?? 0) + 1);
    }
    return counts;
  }, [allMessages]);

  const messages = useMemo(() => {
    if (!isTender || activeSibling === ALL_SIBLINGS) return allMessages;
    // Keep the active sibling's messages plus any project-wide ones
    // (rfq_sid: null) — those apply to every vendor in the tender.
    return allMessages.filter(
      (m) => m.rfq_sid === activeSibling || m.rfq_sid == null,
    );
  }, [allMessages, isTender, activeSibling]);

  // Context picker — defaults to first context, user can change
  const [selectedContextIdx, setSelectedContextIdx] = useState(0);
  useEffect(() => { setSelectedContextIdx(0); }, [entityId]);

  const activeContext = composeContexts[selectedContextIdx] ?? null;
  type OdooModel = 'project.project' | 'purchase.order' | 'purchase.requisition' | 'res.partner';
  const contextModel = (activeContext?.model ?? chatData?.context_model ?? null) as OdooModel | null;
  const contextId = activeContext?.id ?? chatData?.context_id ?? null;

  const invalidateChat = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['strategy-data', 'chat'] });
  }, [queryClient]);

  const handleSendWhatsApp = useCallback(
    async (phone: string, message: string, media?: WhatsAppMediaAttachment[]) => {
      if (!contextId || !contextModel) return { success: false, error: 'Context not loaded' };
      try {
        const result = await sendWhatsApp.mutateAsync({
          to: phone,
          message,
          context_model: contextModel,
          context_id: contextId,
          media: media && media.length > 0 ? media : undefined,
        });
        if (result.success) invalidateChat();
        return { success: result.success, error: result.whatsapp_error };
      } catch (err: unknown) {
        return { success: false, error: err instanceof Error ? err.message : 'Send failed' };
      }
    },
    [contextId, contextModel, sendWhatsApp, invalidateChat],
  );

  const handlePostChatter = useCallback(
    async (message: string) => {
      if (!contextId || !contextModel) return { success: false, error: 'Context not loaded' };
      try {
        const result = await postChatter.mutateAsync({
          message,
          context_model: contextModel,
          context_id: contextId,
        });
        if (result.success) invalidateChat();
        return result;
      } catch (err: unknown) {
        return { success: false, error: err instanceof Error ? err.message : 'Post failed' };
      }
    },
    [contextId, contextModel, postChatter, invalidateChat],
  );

  if (!entityType || !entityId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Select an item to see messages</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-8 w-8 mb-2 animate-spin" />
        <p className="text-sm">Loading messages...</p>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-sm text-destructive">{String(error)}</div>;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Messages</span>
          {chatData?.entity_name && (
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              — {chatData.entity_name}
            </span>
          )}
          {isTender && chatData?.x_rfq_group_id && (
            <span className="text-[10px] text-muted-foreground font-mono truncate">
              {chatData.x_rfq_group_id}
            </span>
          )}
        </div>
        <Badge variant="secondary" className="text-[10px] px-1.5">
          {messages.length}
          {isTender && messages.length !== allMessages.length && (
            <span className="text-muted-foreground/70 ml-0.5">/{allMessages.length}</span>
          )}
        </Badge>
      </div>

      {/* Sibling tabs — only when this is a multi-vendor tender */}
      {isTender && (
        <div className="border-b border-border bg-muted/20 overflow-x-auto scrollbar-thin">
          <div className="flex gap-1 px-2 py-1.5 min-w-max">
            <SiblingTab
              active={activeSibling === ALL_SIBLINGS}
              label="All"
              count={allMessages.length}
              onClick={() => setActiveSibling(ALL_SIBLINGS)}
            />
            {siblings.map((s) => (
              <SiblingTab
                key={s.rfq_sid}
                active={activeSibling === s.rfq_sid}
                label={s.vendor_name || s.rfq_name || s.rfq_sid}
                count={countsBySibling.get(s.rfq_sid) ?? 0}
                onClick={() => setActiveSibling(s.rfq_sid)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Messages — scrollable */}
      <div className="flex-1 overflow-auto scrollbar-thin">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
            <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const initial = (msg.author_name || '?')[0].toUpperCase();
            const attachments = parseAttachments(msg.attachment_urls, msg.attachment_metadata);

            return (
              <div
                key={msg.cdk}
                className={cn(
                  'flex gap-3 px-3 py-3 hover:bg-accent/30 transition-colors',
                  idx < messages.length - 1 && 'border-b border-border/50',
                )}
              >
                <div className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[10px] font-semibold',
                  avatarColor(msg.author_name || ''),
                )}>
                  {initial}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium truncate">{msg.author_name}</span>
                    {msg.platform && <PlatformBadge platform={msg.platform} />}
                    {/* Vendor pill — only on "All" tab where the user
                       can't otherwise tell which sibling the message
                       belongs to. */}
                    {isTender && activeSibling === ALL_SIBLINGS && msg.vendor_name && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate">
                        {msg.vendor_name}
                      </span>
                    )}
                    {msg.created_date && (
                      <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
                        {formatTimestamp(msg.created_date)}
                      </span>
                    )}
                  </div>
                  <TranslatedBody msg={msg} />
                  {attachments.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {attachments.map((att, i) => {
                        const Icon = getFileIcon(att.mimetype);
                        const eligible = DI_ELIGIBLE_MIMETYPES.has(att.mimetype);
                        const extractingThis = extractMutation.isPending
                          && extractMutation.variables?.message_cdk === msg.cdk
                          && (extractMutation.variables?.attachment_index ?? 0) === i;
                        return (
                          <div key={i} className="relative group">
                            <a
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-[10px] text-primary hover:underline bg-primary/5 border border-primary/20 rounded-lg px-2 py-1 transition-colors hover:bg-primary/10"
                            >
                              <Icon className="h-3 w-3 shrink-0" />
                              <span className="truncate max-w-[120px]">{att.filename}</span>
                              {att.size > 0 && (
                                <span className="text-muted-foreground shrink-0">{formatSize(att.size)}</span>
                              )}
                            </a>
                            {eligible && (
                              <button
                                title="Extract with Document Intelligence"
                                disabled={extractMutation.isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  extractMutation.mutate(
                                    { message_cdk: msg.cdk, attachment_index: i },
                                    {
                                      onSuccess: (res) => {
                                        if (res.success) {
                                          toast.success(
                                            `Extracted: ${res.matched_line_count} matched, ${res.unmatched_line_count} unmatched`,
                                          );
                                        } else {
                                          toast.error(res.error || 'Extraction failed');
                                        }
                                      },
                                      onError: (err) => {
                                        toast.error(err.message || 'Extraction failed');
                                      },
                                    },
                                  );
                                }}
                                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-primary/90 disabled:opacity-50"
                              >
                                {extractingThis
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <ScanLine className="h-3 w-3" />
                                }
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Compose — pinned at bottom, channels from BFF */}
      {channels.length > 0 && (
        <MessengerTabsSection
          channels={channels}
          collapsedByDefault
          contacts={contacts}
          contactsLoading={isLoading}
          onSendWhatsApp={channels.includes('whatsapp') ? handleSendWhatsApp : undefined}
          onPostToChatter={channels.includes('chatter') ? handlePostChatter : undefined}
          isSendingWhatsApp={sendWhatsApp.isPending}
          isPostingChatter={postChatter.isPending}
          contextPicker={composeContexts.length > 1 ? (
            <ContextPicker
              contexts={composeContexts}
              selectedIdx={selectedContextIdx}
              onSelect={setSelectedContextIdx}
            />
          ) : undefined}
        />
      )}
    </div>
  );
}


// ── Context Picker (autocomplete) ────────────────────────────────

function ContextPicker({
  contexts,
  selectedIdx,
  onSelect,
}: {
  contexts: ComposeContext[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = contexts[selectedIdx];
  const filtered = query
    ? contexts.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()))
    : contexts;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const TYPE_LABELS: Record<string, string> = {
    vendor: 'Company',
    contact: 'Contact',
    customer: 'Customer',
    project: 'Project',
    rfq: 'RFQ',
    po: 'PO',
  };

  return (
    <div className="mb-3" ref={containerRef}>
      <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide block mb-1">
        Post to
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={open ? query : `${selected?.label ?? ''}`}
          placeholder="Search..."
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          className="w-full text-xs px-2 py-1.5 border border-border/50 rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary/30"
        />
        {!open && selected && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
            {TYPE_LABELS[selected.type] ?? selected.type}
          </span>
        )}
        {open && filtered.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-popover border border-border rounded-md shadow-md max-h-32 overflow-auto">
            {filtered.map((ctx, i) => {
              const realIdx = contexts.indexOf(ctx);
              return (
                <button
                  key={ctx.sid}
                  onClick={() => { onSelect(realIdx); setOpen(false); setQuery(''); }}
                  className={cn(
                    'w-full text-left px-2 py-1.5 text-xs hover:bg-accent/50 flex items-center justify-between',
                    realIdx === selectedIdx && 'bg-accent/30',
                  )}
                >
                  <span className="truncate">{ctx.label}</span>
                  <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                    {TYPE_LABELS[ctx.type] ?? ctx.type}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sibling tab pill (per-vendor selector for tender chat) ─────────

interface SiblingTabProps {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}

function SiblingTab({ active, label, count, onClick }: SiblingTabProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors shrink-0',
        active
          ? 'bg-primary text-primary-foreground'
          : 'bg-card text-muted-foreground hover:text-foreground hover:bg-accent border border-border',
      )}
    >
      <span className="truncate max-w-[120px]">{label}</span>
      <span
        className={cn(
          'text-[10px] tabular-nums px-1 rounded',
          active ? 'bg-primary-foreground/20' : 'bg-muted',
        )}
      >
        {count}
      </span>
    </button>
  );
}
